import React, { useState, useEffect } from 'react';
import { X, Trophy, Plus, Trash2 } from 'lucide-react';
import { apiGet, apiPost } from '../../utils/api';
import UserSearchInput from './UserSearchInput';

const CreateLeaderboardModal = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [addedUsers, setAddedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await apiGet('/leaderboards/available_metrics');
      if (response.ok) {
        const data = await response.json();
        setAvailableMetrics(data.metrics || []);
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
    }
  };

  const toggleMetric = (key) => {
    setSelectedMetrics(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleAddUser = (user) => {
    if (!addedUsers.find(u => u.public_id === user.public_id)) {
      setAddedUsers(prev => [...prev, user]);
    }
  };

  const handleRemoveUser = (publicId) => {
    setAddedUsers(prev => prev.filter(u => u.public_id !== publicId));
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Please enter a name for the leaderboard');
      return;
    }
    if (selectedMetrics.length === 0) {
      setError('Please select at least one metric');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await apiPost('/leaderboards', {
        name: name.trim(),
        metric_keys: selectedMetrics,
        invite_user_ids: addedUsers.map(u => u.public_id)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create leaderboard');
      }

      onCreate();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Trophy size={20} className="text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-800 font-unna">Create Leaderboard</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-5">
          {error && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm font-rasa">{error}</div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-rasa">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Q2 Challenge, Team Sprint..."
              className="w-full px-3 py-2 bg-gray-50 text-gray-900 border border-gray-300 rounded-lg text-sm font-rasa focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Metrics */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 font-rasa">Metrics</label>
            <div className="grid grid-cols-2 gap-2">
              {availableMetrics.map(m => (
                <button
                  key={m.key}
                  onClick={() => toggleMetric(m.key)}
                  className={`px-3 py-2 rounded-lg text-sm font-rasa text-left transition-colors ${
                    selectedMetrics.includes(m.key)
                      ? 'bg-blue-100 text-blue-700 border border-blue-300'
                      : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Add Members */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 font-rasa">Add Members</label>
            <UserSearchInput
              onSelect={handleAddUser}
              excludeIds={addedUsers.map(u => u.public_id)}
            />
            {addedUsers.length > 0 && (
              <div className="mt-2 space-y-1">
                {addedUsers.map(user => (
                  <div key={user.public_id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                    <div className="flex items-center gap-2">
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold flex-shrink-0">
                          {(user.full_name || user.username || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <span className="text-sm text-gray-700 font-rasa">{user.full_name || user.username}</span>
                        {user.email && <p className="text-xs text-gray-400 font-rasa">{user.email}</p>}
                      </div>
                    </div>
                    <button onClick={() => handleRemoveUser(user.public_id)} className="text-gray-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1 font-rasa">You are added automatically. Others receive an invitation they must accept.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-rasa"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50 font-rasa flex items-center gap-1"
          >
            {loading ? 'Creating...' : <><Plus size={14} /> Create</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateLeaderboardModal;
